import { BlockId } from './BlockType.js';

/**
 * Portal system for Nether dimension travel.
 *
 * A valid nether portal frame:
 *   - 4 blocks wide x 5 blocks tall of OBSIDIAN
 *   - Inner area is 2 wide x 3 tall (air or portal blocks)
 *   - Frame looks like:
 *
 *     OOOO
 *     O  O
 *     O  O
 *     O  O
 *     OOOO
 *
 * Where O = obsidian, spaces = portal fill area
 */

/**
 * Detect if there's a valid obsidian portal frame near the given position.
 * Scans nearby blocks to find the frame origin.
 *
 * @param {object} world - World object with getBlock()
 * @param {number} x - World X position to check near
 * @param {number} y - World Y position to check near
 * @param {number} z - World Z position to check near
 * @returns {{ isPortal: boolean, portalBlocks: Array, frameOrigin: {x,y,z}, axis: string }}
 */
export function detectPortal(world, x, y, z) {
  // Search a small area around the given position for a portal frame
  for (let dx = -4; dx <= 4; dx++) {
    for (let dy = -5; dy <= 5; dy++) {
      for (let dz = -4; dz <= 4; dz++) {
        const ox = x + dx;
        const oy = y + dy;
        const oz = z + dz;

        // Try frame aligned with X axis (portal faces X-Z, depth along Z)
        const resultX = _checkFrame(world, ox, oy, oz, 'x');
        if (resultX.isPortal) return resultX;

        // Try frame aligned with Z axis (portal faces X-Z, depth along X)
        const resultZ = _checkFrame(world, ox, oy, oz, 'z');
        if (resultZ.isPortal) return resultZ;
      }
    }
  }

  return { isPortal: false, portalBlocks: [], frameOrigin: null, axis: null };
}

/**
 * Check if there's a valid 4-wide x 5-tall obsidian frame starting at (ox, oy, oz).
 * The width extends along the specified axis.
 */
function _checkFrame(world, ox, oy, oz, axis) {
  const portalBlocks = [];

  // Check all 4x5 positions
  for (let w = 0; w < 4; w++) {
    for (let h = 0; h < 5; h++) {
      const wx = axis === 'x' ? ox + w : ox;
      const wy = oy + h;
      const wz = axis === 'z' ? oz + w : oz;

      const isEdge = w === 0 || w === 3 || h === 0 || h === 4;
      const block = world.getBlock(wx, wy, wz);

      if (isEdge) {
        // Frame edges must be obsidian
        if (block !== BlockId.OBSIDIAN) {
          return { isPortal: false, portalBlocks: [], frameOrigin: null, axis: null };
        }
      } else {
        // Inner area must be air or already portal
        if (block !== BlockId.AIR && block !== BlockId.NETHER_PORTAL) {
          return { isPortal: false, portalBlocks: [], frameOrigin: null, axis: null };
        }
        portalBlocks.push({ x: wx, y: wy, z: wz });
      }
    }
  }

  return { isPortal: true, portalBlocks, frameOrigin: { x: ox, y: oy, z: oz }, axis };
}

/**
 * Activate a portal: fill the inner area with NETHER_PORTAL blocks.
 *
 * @param {object} world - World object with setBlock()
 * @param {number} x - A position near the portal frame
 * @param {number} y
 * @param {number} z
 * @returns {boolean} - true if portal was activated
 */
export function activatePortal(world, x, y, z) {
  const result = detectPortal(world, x, y, z);
  if (!result.isPortal) return false;

  for (const block of result.portalBlocks) {
    world.setBlock(block.x, block.y, block.z, BlockId.NETHER_PORTAL);
  }

  return true;
}

/**
 * Check if the player is standing inside a portal block.
 *
 * @param {{ x: number, y: number, z: number }} playerPos - Player position
 * @param {object} world - World object with getBlock()
 * @returns {boolean} - true if player feet block is NETHER_PORTAL
 */
export function checkPortalEntry(playerPos, world) {
  const bx = Math.floor(playerPos.x);
  const by = Math.floor(playerPos.y);
  const bz = Math.floor(playerPos.z);

  // Check at feet and one block above (player is ~2 blocks tall)
  return world.getBlock(bx, by, bz) === BlockId.NETHER_PORTAL ||
         world.getBlock(bx, by + 1, bz) === BlockId.NETHER_PORTAL;
}

/**
 * Build a portal frame at the given position (for auto-generating return portals).
 *
 * @param {object} world - World with setBlock()
 * @param {number} x - Bottom-left corner X
 * @param {number} y - Bottom-left corner Y
 * @param {number} z - Bottom-left corner Z
 * @param {string} axis - 'x' or 'z', which direction the frame width extends
 */
export function buildPortalFrame(world, x, y, z, axis = 'x') {
  for (let w = 0; w < 4; w++) {
    for (let h = 0; h < 5; h++) {
      const wx = axis === 'x' ? x + w : x;
      const wy = y + h;
      const wz = axis === 'z' ? z + w : z;

      const isEdge = w === 0 || w === 3 || h === 0 || h === 4;
      if (isEdge) {
        world.setBlock(wx, wy, wz, BlockId.OBSIDIAN);
      } else {
        world.setBlock(wx, wy, wz, BlockId.NETHER_PORTAL);
      }
    }
  }
}

// ============================================================
// END PORTAL SYSTEM
// ============================================================

/**
 * End portal frame ring positions relative to center (0,0).
 * 12 blocks forming a 5x5 ring without corners, on the XZ plane.
 *
 *    .FFF.
 *    F   F
 *    F   F
 *    F   F
 *    .FFF.
 *
 * Center 3x3 is the portal area.
 */
const END_FRAME_OFFSETS = [
  // North side (z = -2)
  { x: -1, z: -2 }, { x: 0, z: -2 }, { x: 1, z: -2 },
  // South side (z = +2)
  { x: -1, z: 2 }, { x: 0, z: 2 }, { x: 1, z: 2 },
  // West side (x = -2)
  { x: -2, z: -1 }, { x: -2, z: 0 }, { x: -2, z: 1 },
  // East side (x = +2)
  { x: 2, z: -1 }, { x: 2, z: 0 }, { x: 2, z: 1 },
];

const END_PORTAL_INNER = [
  { x: -1, z: -1 }, { x: 0, z: -1 }, { x: 1, z: -1 },
  { x: -1, z: 0 },  { x: 0, z: 0 },  { x: 1, z: 0 },
  { x: -1, z: 1 },  { x: 0, z: 1 },  { x: 1, z: 1 },
];

/**
 * Check if player is standing on an active End Portal block.
 */
export function checkEndPortalEntry(playerPos, world) {
  const bx = Math.floor(playerPos.x);
  const by = Math.floor(playerPos.y) - 1;
  const bz = Math.floor(playerPos.z);

  // Check feet position and below
  for (let dy = 0; dy >= -1; dy--) {
    const block = world.getBlock(bx, by + dy, bz);
    if (block === BlockId.END_PORTAL) return true;
  }
  return false;
}

/**
 * Build an End portal frame ring at the given center position.
 * Used in Stronghold generation and for the exit portal.
 * @param {boolean} activate - If true, also fill the center with END_PORTAL blocks
 */
export function buildEndPortalFrame(world, cx, cy, cz, activate = false) {
  // Place frame blocks
  for (const offset of END_FRAME_OFFSETS) {
    world.setBlock(cx + offset.x, cy, cz + offset.z, BlockId.END_PORTAL_FRAME);
  }

  if (activate) {
    // Fill center 3x3 with portal blocks
    for (const offset of END_PORTAL_INNER) {
      world.setBlock(cx + offset.x, cy, cz + offset.z, BlockId.END_PORTAL);
    }
  }
}

/**
 * Check if all 12 frame positions around a center have eyes placed.
 * @param {Object} tileEntities - TileEntities instance to check eye state
 */
export function isEndPortalComplete(world, cx, cy, cz, tileEntities) {
  for (const offset of END_FRAME_OFFSETS) {
    const fx = cx + offset.x;
    const fz = cz + offset.z;
    const block = world.getBlock(fx, cy, fz);
    if (block !== BlockId.END_PORTAL_FRAME) return false;
    // Check if this frame has an eye
    const te = tileEntities.data.get(`${fx},${cy},${fz}`);
    if (!te || !te.hasEye) return false;
  }
  return true;
}

/**
 * Activate an End portal by filling the center with portal blocks.
 */
export function activateEndPortal(world, cx, cy, cz) {
  for (const offset of END_PORTAL_INNER) {
    world.setBlock(cx + offset.x, cy, cz + offset.z, BlockId.END_PORTAL);
  }
}

/**
 * Build the exit portal in The End (bedrock ring + portal center).
 * Placed after the dragon is defeated.
 */
export function buildEndExitPortal(world, cx, cy, cz) {
  // Bedrock base ring (same shape as frame)
  for (const offset of END_FRAME_OFFSETS) {
    world.setBlock(cx + offset.x, cy, cz + offset.z, BlockId.BEDROCK);
  }
  // Portal blocks in center
  for (const offset of END_PORTAL_INNER) {
    world.setBlock(cx + offset.x, cy, cz + offset.z, BlockId.END_PORTAL);
  }
  // Bedrock pillar in center with dragon egg on top
  world.setBlock(cx, cy + 1, cz, BlockId.BEDROCK);
  world.setBlock(cx, cy + 2, cz, BlockId.BEDROCK);
  world.setBlock(cx, cy + 3, cz, BlockId.BEDROCK);
  world.setBlock(cx, cy + 4, cz, BlockId.DRAGON_EGG);
}

/** Get the frame offsets for external use (e.g., placing eyes) */
export function getEndFrameOffsets() {
  return END_FRAME_OFFSETS;
}
